import { useEffect, useContext, useRef } from "react"
import { useLocation } from "react-router-dom"
import {
  ADD_DYNAMIC_ELEMENT,
  DELETE_DYNAMIC_ELEMENT,
  SET_ALL_DYNAMIC_ELEMENTS,
  SET_ALL_DYNAMIC_ELEMENTS_TO_INITIAL_STATE,
  SET_ELEMENT_TO_DELETE,
  SET_IS_FROM_PROJECT,
  SET_IS_HOW_TO_ADD_MODAL_OPEN,
  SET_LOADING,
  SET_OPEN_CONFIRM_DELETE_MODAL,
  SET_PARTS_RELATED_TO_ELEMENT_TO_DELETE,
} from "context/types"
import DynamicElementsContext from "context/dynamicElements/DynamicElementsContext"
import { API, Auth } from "aws-amplify"
import useLocalStorage from "hooks/useLocalStorage"

const CORE = "CORE"
/**
 * Constant representing the source "bank of prices" for converting dynamic elements.
 * Used to indicate that the elements come from the price bank.
 */
export const FROM_BANK = "bank"

/**
 * Constant representing the source "project in budgeting" for converting dynamic elements.
 * Used to indicate that the elements belong to a specific project in the budgeting page.
 */
export const FROM_PROJECT = "project"

export const useDynamicElements = () => {
  const context = useContext(DynamicElementsContext)
  const [dynamicElementsConfig, setDynamicElementsConfig] = useLocalStorage(
    "dynamicElements",
    {
      showProjectInitialModal: true,
      showBankPriceInitialModal: true,
    },
  )

  if (!context) {
    throw new Error(
      "useDynamicElements must be used within a DynamicElementsProvider",
    )
  }

  const { state, dispatch } = context

  const location = useLocation()
  const pathname = location?.pathname || ""
  const isBankPath = pathname.includes("bancodeprecios/dynamic-element")

  // const { dynamicElements, dispatches, loading } = context

  // useEffect(() => {
  //   const fetchData = async () => {
  //     if (dynamicElements.length === 0 && organizationId) {
  //       await dispatches.getDynamicElements(organizationId)
  //     }
  //   }

  //   if (organizationId) {
  //     fetchData()
  //   }

  //   return () => {
  //     dispatches.setDynamicElementsToInitialState()
  //   }
  // }, [organizationId])

  // return { loading, dynamicElements }
  const setLoading = isLoading => {
    dispatch({
      type: SET_LOADING,
      payload: isLoading,
    })
  }

  const lastPathRef = useRef(null)

  const setIsFromProject = isFromProject => {
    dispatch({
      type: SET_IS_FROM_PROJECT,
      payload: isFromProject,
    })
  }

  useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname
    }

    setIsFromProject(!isBankPath)
  }, [isBankPath, pathname])

  const getCommonConfig = async () => {
    const generalConfig = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    return generalConfig
  }

  const getDynamicElements = async poolId => {
    if (!poolId) {
      console.error("Invalid poolId provided.")
      return
    }

    try {
      setLoading(true)
      const configurations = await getCommonConfig()
      const respDynamicElements = await API.get(
        CORE,
        `/decompositions/?poolID=${poolId}`,
        configurations,
      )

      dispatch({
        type: SET_ALL_DYNAMIC_ELEMENTS,
        payload: {
          data: respDynamicElements,
        },
      })
    } catch (error) {}
  }

  const addElement = async (element, poolId) => {
    try {
      const configurations = await getCommonConfig()
      const bodyElement = {
        type: element.type,
        description: element.description,
        unity: element.unity,
        price: element.price,
        code: element.code,
      }
      if (element.priceReduced !== undefined && element.priceReduced !== "") {
        bodyElement.priceReduced = element.priceReduced
      }
      const body = [bodyElement]

      const payload = {
        ...configurations,
        body: body,
      }
      const respDynamicElements = await API.post(
        CORE,
        `/decompositions/?poolID=${poolId}`,
        payload,
      )

      const id = respDynamicElements[0]
      const newData = {
        itemId: id,
        type: element.type,
        description: element.description,
        unity: element.unity,
        price: element.price,
        code: element.code,
      }
      if (element.priceReduced !== undefined && element.priceReduced !== "") {
        newData.priceReduced = element.priceReduced
      }

      dispatch({
        type: ADD_DYNAMIC_ELEMENT,
        payload: {
          element: newData,
        },
      })
      return true //is success
    } catch (error) {
      return false
    }
  }

  const deleteElement = async (element, poolId) => {
    try {
      setLoading(true)

      const configurations = await getCommonConfig()

      const path = `/decompositions/${
        state.isFromProject ? "project?projectID" : "bank?bankID"
      }=${poolId}&items_ids=${element.itemId}`

      const response = await API.del(CORE, path, configurations)

      if (Object.keys(response).length === 0) {
        const dynamicElementsFiltered = state.dynamicElements.filter(
          el => el.itemId !== element.itemId,
        )

        dispatch({
          type: SET_ALL_DYNAMIC_ELEMENTS,
          payload: {
            data: dynamicElementsFiltered,
          },
        })

        setConfirmDeleteModal(false)
      }

      setLoading(false)

      return true //is success
    } catch (error) {
      const status = error?.response?.status
      const data = error?.response?.data

      if (status === 409 && data) {
        dispatch({
          type: SET_PARTS_RELATED_TO_ELEMENT_TO_DELETE,
          payload: data[element.itemId],
        })
      } else {
        console.error("Error inesperado al borrar el elemento:", error)
      }

      return false // is success
    }
  }

  const editElement = async (element, poolId, from) => {
    try {
      setLoading()

      const configurations = await getCommonConfig()
      const bodyElementEdit = {
        itemId: element.itemId,
        type: element.type,
        description: element.description,
        unity: element.unity,
        price: element.price,
        code: element.code,
      }
      if (element.priceReduced !== undefined && element.priceReduced !== "") {
        bodyElementEdit.priceReduced = element.priceReduced
      }
      const body = [bodyElementEdit]

      const payload = {
        ...configurations,
        body: body,
      }

      const useProject = from ? from === FROM_PROJECT : state.isFromProject
      const path = `/decompositions/${useProject ? "project?projectID" : "bank?bankID"}=${poolId}`
      const putResult = await API.put(CORE, path, payload)

      const newDynamicElements = state.dynamicElements.map(e => {
        if (e.itemId === element.itemId) {
          const updated = {
            ...e,
            type: element.type,
            description: element.description,
            unity: element.unity,
            price: element.price,
            code: element.code,
          }
          if (
            element.priceReduced !== undefined &&
            element.priceReduced !== ""
          ) {
            updated.priceReduced = element.priceReduced
          } else {
            delete updated.priceReduced
          }
          return updated
        }
        return e
      })
      dispatch({
        type: SET_ALL_DYNAMIC_ELEMENTS,
        payload: {
          data: newDynamicElements,
        },
      })

      return true //is success
    } catch (error) {
      return false // is success
    } finally {
      setLoading(false)
    }
  }

  const setElementToDelete = element => {
    dispatch({
      type: SET_ELEMENT_TO_DELETE,
      payload: element,
    })
  }

  const setConfirmDeleteModal = isModalOpen => {
    dispatch({
      type: SET_OPEN_CONFIRM_DELETE_MODAL,
      payload: isModalOpen,
    })
  }

  const setIsHowToAddModalOpen = isModalOpen => {
    dispatch({
      type: SET_IS_HOW_TO_ADD_MODAL_OPEN,
      payload: isModalOpen,
    })
  }

  /**
   * Fetches and converts dynamic elements from a given source (price bank or budgeting project)
   * into the target pool context.
   *
   * @param {string|number} poolId - The ID of the target pool where elements will be converted to.
   * If `from` is `"bank"`, this should be the organization ID.
   * If `from` is `"project"`, this should be the project ID.
   * @param {"bank"|"project"} from - The source of the dynamic elements. Must be either FROM_BANK or FROM_PROJECT.
   * @returns {void}
   */
  const getDynamicElementsConvertFrom = async (poolId, from) => {
    setDynamicElementsConfig(prev => ({
      ...prev,
      [state.isFromProject
        ? "showProjectInitialModal"
        : "showBankPriceInitialModal"]: false,
    }))

    if (!poolId) {
      console.error("Invalid poolId provided.")
      return
    }

    try {
      setLoading()
      const configurations = await getCommonConfig()
      const respDynamicElements = await API.post(
        CORE,
        `/decompositions/convert_from_${from}?poolID=${poolId}`,
        configurations,
      )

      dispatch({
        type: SET_ALL_DYNAMIC_ELEMENTS_TO_INITIAL_STATE,
      })

      dispatch({
        type: SET_ALL_DYNAMIC_ELEMENTS,
        payload: {
          data: respDynamicElements,
        },
      })
    } catch (error) {
      console.error("Error fetching dynamic elements:", error)
    }
  }

  const setDynamicElementsToInitialState = () => {
    dispatch({
      type: SET_ALL_DYNAMIC_ELEMENTS_TO_INITIAL_STATE,
    })
  }

  const setPartsRelatedToElementToDelete = partsRelated => {
    dispatch({
      type: SET_PARTS_RELATED_TO_ELEMENT_TO_DELETE,
      payload: partsRelated,
    })
  }

  return {
    dynamicElements: state.dynamicElements,
    isFromProject: state.isFromProject,
    updating: state.updating,
    loading: state.loading,
    deleteElement: {
      openConfirmModal: state.deleteElement.openConfirmModal,
      elementToDelete: state.deleteElement.elementToDelete,
      partsRelated: state.deleteElement.partsRelated,
    },
    isHowToAddModalOpen: state.isHowToAddModalOpen,
    dynamicElementsConfig,
    dispatches: {
      setIsFromProject,
      setLoading,
      getDynamicElements,
      addElement,
      deleteElement,
      editElement,
      setElementToDelete,
      setConfirmDeleteModal,
      setIsHowToAddModalOpen,
      getDynamicElementsConvertFrom,
      setDynamicElementsToInitialState,
      setPartsRelatedToElementToDelete,
      setDynamicElementsConfig,
    },
  }
}
